/**
 * Bundles in the class diagram: the links from one package to another drawn as **one** thick
 * arrow with a count, like the nodal view's bundles (same rules, same keys: `../bundles.ts`), so
 * the inspector lists the same links whichever view selected the bundle.
 *
 * A bundle opens into its arrows when the user looks at it: it is selected, it holds the
 * selected link, or the selected package is one of its ends (or inside or around one). The
 * arrows of the box looked at are always drawn one by one.
 */
import type { LinkKind } from "@protocol/LinkKind";
import { bundleLinks, parseEnd, type Bundle, type BundleEnd } from "../bundles";
import type { Selection } from "../view";
import type { DiagramBox, DiagramEdge, DiagramPackage } from "./layout";
import { COUNTS_ALWAYS, COUNT_BUDGET, COUNT_MIN_PX } from "../constants";
import { ANGLE_SLICE, BUNDLE_LANE } from "./constants";

/** A bundle of arrows between two packages (or a package and a box). */
export interface DiagramBundle extends Bundle<DiagramEdge> {
  /** The kinds of its links (one kind: drawn in that kind's colour). */
  kinds: LinkKind[];
  /** Where its arrow starts (on the `from` end's border). */
  x1: number;
  /** Where its arrow starts. */
  y1: number;
  /** Where its arrow ends (on the `to` end's border, the arrowhead). */
  x2: number;
  /** Where its arrow ends. */
  y2: number;
}

/** What the user is looking at: decides which bundles open. */
export interface DiagramFocus {
  /** The hovered or selected box. */
  node: string | null;
  /** The selected link's ends and kind. */
  link: { from: string; to: string; kind: LinkKind; pin?: string | null } | null;
  /** The selected package. */
  group: string | null;
  /** The selected bundle's key. */
  bundle: string | null;
}

/** The focus of a selection (the hovered box, when there is one, is the caller's to add). */
export function selectionFocus(s: Selection): DiagramFocus {
  return {
    node: s?.type === "node" ? s.id : null,
    link: s?.type === "link" ? s.link : null,
    group: s?.type === "group" ? s.id : null,
    bundle: s?.type === "bundle" ? s.key : null,
  };
}

/** The last {@link countedBundles} of each list of closed bundles, with the zoom it was for. */
const lastCounted = new WeakMap<DiagramBundle[], { k: number; counted: Set<DiagramBundle> | null }>();

/**
 * The closed `bundles` whose count is drawn (and clicked) at zoom `k`; `null`: all of them.
 * The same for the painting and the pointer.
 */
export function countedBundles(bundles: DiagramBundle[], k: number): Set<DiagramBundle> | null {
  if (bundles.length <= COUNTS_ALWAYS) return null;
  const last = lastCounted.get(bundles);
  if (last && last.k === k) return last.counted;
  const long = bundles.filter((b) => Math.hypot(b.x2 - b.x1, b.y2 - b.y1) * k >= COUNT_MIN_PX);
  const counted = new Set(long.sort((a, b) => b.links.length - a.links.length).slice(0, COUNT_BUDGET));
  lastCounted.set(bundles, { k, counted });
  return counted;
}

/** Nothing looked at. */
export const NO_FOCUS: DiagramFocus = { node: null, link: null, group: null, bundle: null };

/** Where the segment from a rectangle's centre towards `(tx, ty)` leaves it. */
export function border(r: { x: number; y: number; w: number; h: number }, tx: number, ty: number): [number, number] {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const dx = tx - cx;
  const dy = ty - cy;
  if (!dx && !dy) return [cx, cy];
  const scale = Math.min(dx ? r.w / 2 / Math.abs(dx) : Infinity, dy ? r.h / 2 / Math.abs(dy) : Infinity);
  return [cx + dx * scale, cy + dy * scale];
}

/**
 * The bundles of `edges`: links leaving a package for another, at least two per pair of ends,
 * each drawn from border to border of its two ends (a package's frame or a box).
 */
export function diagramBundles(edges: DiagramEdge[], byId: Map<string, DiagramBox>, packageById: Map<string, DiagramPackage>): DiagramBundle[] {
  const groupOf = (id: string) => {
    const g = byId.get(id)?.node.group;
    return g && packageById.has(g) ? g : null;
  };
  const parentOf = (id: string) => packageById.get(id)?.parent ?? null;
  /** An end's rectangle: the package (its frame below the tab counts too) or the box. */
  const rectOf = (end: BundleEnd) => {
    const { kind, id } = parseEnd(end);
    return kind === "group" ? packageById.get(id) : byId.get(id);
  };
  /**
   * Lines already drawn, by direction (one slice of {@link ANGLE_SLICE} radians, either way
   * along): a new bundle lying along one of them moves aside. Only the slices next to a new
   * line's are looked through, not every bundle (a big project has thousands).
   */
  const drawn = new Map<number, { ax: number; ay: number; ux: number; uy: number }[]>();
  const sliceOf = (ux: number, uy: number) => {
    const turn = Math.atan2(uy, ux);
    return Math.floor((turn < 0 ? turn + Math.PI : turn) / ANGLE_SLICE);
  };
  const slices = Math.ceil(Math.PI / ANGLE_SLICE);
  return bundleLinks(edges, (e) => e.link, groupOf, parentOf).flatMap((b) => {
    const a = rectOf(b.from);
    const c = rectOf(b.to);
    if (!a || !c) return [];
    const ax = a.x + a.w / 2;
    const ay = a.y + a.h / 2;
    const cx = c.x + c.w / 2;
    const cy = c.y + c.h / 2;
    const len = Math.hypot(cx - ax, cy - ay) || 1;
    const ux = (cx - ax) / len;
    const uy = (cy - ay) / len;
    // Packages stacked in a column put many bundles on one line: each one lying along an
    // earlier one (same direction either way, close to its line) moves to the next lane.
    const slice = sliceOf(ux, uy);
    let lane = 0;
    for (const near of new Set([(slice + slices - 1) % slices, slice % slices, (slice + 1) % slices])) {
      for (const d of drawn.get(near) ?? []) if (Math.abs(d.ux * uy - d.uy * ux) < 0.15 && Math.abs((ax - d.ax) * d.uy - (ay - d.ay) * d.ux) < BUNDLE_LANE / 2) lane++;
    }
    const own = drawn.get(slice % slices);
    if (own) own.push({ ax, ay, ux, uy });
    else drawn.set(slice % slices, [{ ax, ay, ux, uy }]);
    // A little to the right of its direction, so A → B and B → A do not cover each other.
    const side = 8 + (lane % 2 ? -1 : 1) * Math.ceil(lane / 2) * BUNDLE_LANE;
    const ox = -uy * side;
    const oy = ux * side;
    const [x1, y1] = border(a, cx + ox, cy + oy);
    const [x2, y2] = border(c, ax + ox, ay + oy);
    const kinds = [...new Set(b.links.map((e) => e.link.kind))];
    return [{ ...b, kinds, x1: x1 + ox, y1: y1 + oy, x2: x2 + ox, y2: y2 + oy }];
  });
}

/** Whether package `group` is the end, sits inside it, or holds it. */
function relates(group: string, end: BundleEnd, byId: Map<string, DiagramBox>, packageById: Map<string, DiagramPackage>): boolean {
  const parentOf = (id: string) => packageById.get(id)?.parent ?? null;
  const { kind, id } = parseEnd(end);
  /** Whether `inner` is `outer` or inside it. */
  const within = (inner: string | null, outer: string) => {
    for (let at = inner, steps = 0; at && steps < 1000; at = parentOf(at), steps++) if (at === outer) return true;
    return false;
  };
  if (kind === "node") return within(byId.get(id)?.node.group ?? null, group);
  return within(group, id) || within(id, group);
}

/** Whether `bundle` is drawn as its arrows rather than one (see {@link DiagramFocus}). */
export function isOpen(bundle: DiagramBundle, focus: DiagramFocus, byId: Map<string, DiagramBox>, packageById: Map<string, DiagramPackage>): boolean {
  if (focus.bundle === bundle.key) return true;
  const l = focus.link;
  if (l && bundle.links.some((e) => e.link.from === l.from && e.link.to === l.to && e.link.kind === l.kind && (e.link.pin ?? null) === (l.pin ?? null))) return true;
  return !!focus.group && (relates(focus.group, bundle.from, byId, packageById) || relates(focus.group, bundle.to, byId, packageById));
}

/** What the diagram draws for the arrows: arrows one by one, and the closed bundles. */
export interface ShownArrows {
  /** Arrows drawn on their own: in no bundle, in an open one, or touching the box looked at. */
  edges: DiagramEdge[];
  /** Bundles drawn as one arrow with a count. */
  bundles: DiagramBundle[];
}

/**
 * The last {@link shownArrows} of each layout (by its list of arrows), with the focus it was for:
 * it is asked at every paint and every pointer move, and a big project has tens of thousands of
 * arrows to sort.
 */
const lastShown = new WeakMap<DiagramEdge[], { key: string; shown: ShownArrows }>();

/** Which arrows and bundles are drawn while the user looks at `focus`. */
export function shownArrows(
  layout: { edges: DiagramEdge[]; bundles: DiagramBundle[]; bundleOf: Map<DiagramEdge, DiagramBundle>; byId: Map<string, DiagramBox>; packageById: Map<string, DiagramPackage> },
  focus: DiagramFocus,
): ShownArrows {
  const l = focus.link;
  const key = [focus.node, focus.group, focus.bundle, l && [l.from, l.to, l.kind, l.pin ?? ""].join("\u0000")].join("\u0001");
  const last = lastShown.get(layout.edges);
  if (last && last.key === key) return last.shown;
  const shown = sortArrows(layout, focus);
  lastShown.set(layout.edges, { key, shown });
  return shown;
}

/** {@link shownArrows}, worked out. */
function sortArrows(
  layout: { edges: DiagramEdge[]; bundles: DiagramBundle[]; bundleOf: Map<DiagramEdge, DiagramBundle>; byId: Map<string, DiagramBox>; packageById: Map<string, DiagramPackage> },
  focus: DiagramFocus,
): ShownArrows {
  const open = new Set(layout.bundles.filter((b) => isOpen(b, focus, layout.byId, layout.packageById)));
  const edges = layout.edges.filter((e) => {
    const b = layout.bundleOf.get(e);
    return !b || open.has(b) || (!!focus.node && (e.link.from === focus.node || e.link.to === focus.node));
  });
  return { edges, bundles: layout.bundles.filter((b) => !open.has(b)) };
}
